import React, {useCallback, useMemo, useState} from "react";
import {Card, Space, Typography} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useTranslate} from "../../../hooks/translate.hook";
import {HealthCheckerIncident} from "../../../models/health-checker/health-checker";
import {healthCheckerService} from "../../../services/health-checker/health-checker.service";
import {downloadBlobFile} from "../../../utils/blob.download";
import {SmartTable} from "../../common/smart-table/smart-table";
import {SmartTableDataHandler} from "../../common/smart-table/smart-table.data-handler";
import {incidentsColumnsFactory} from "./issue.incidents.columns";

const {Text} = Typography;

interface IssueIncidentsProps {
    issueId: number;
}

export const IssueIncidents: React.FC<IssueIncidentsProps> = ({issueId}) => {
    const t = useTranslate();
    const [downloadingId, setDownloadingId] = useState<number>();
    const handler = useMemo(
        () => new SmartTableDataHandler<HealthCheckerIncident>(
            (request) => healthCheckerService.getIssueIncidents(issueId, request)
        ),
        [issueId]
    );

    const downloadIncidentData = useCallback(async (incident: HealthCheckerIncident) => {
        setDownloadingId(incident.id);
        try {
            const {blob, filename} = await healthCheckerService.downloadIncidentData(incident.id);
            downloadBlobFile(blob, filename);
        } finally {
            setDownloadingId(undefined);
        }
    }, []);

    const columns = useMemo(
        () => incidentsColumnsFactory(t, downloadIncidentData, downloadingId),
        [t, downloadIncidentData, downloadingId]
    );

    return (
        <Card
            className="health-center-issue__card health-center-issue__incidents"
            title={(
                <Space>
                    <FontAwesomeIcon icon={["fas", "history"]} />
                    {t("HEALTH-CENTER.ISSUE.INCIDENTS.TITLE", {defaultValue: "Incident history"})}
                </Space>
            )}
            extra={(
                <Text type="secondary">
                    {t("HEALTH-CENTER.ISSUE.INCIDENTS.DESCRIPTION-TEXT", {
                        defaultValue: "Every occurrence recorded for this issue",
                    })}
                </Text>
            )}
        >
            <SmartTable handler={handler} columns={columns} />
        </Card>
    );
};
